import type { FormEvent, RefObject } from "react";
import { useState } from "react";
import { useQuery } from "convex/react";
import { MessageCircle } from "lucide-react";
import { FileTypeIcon } from "../FileTypeIcon";
import { ChatPanel } from "../ChatPanel";
import { CmdKOverlay } from "../CmdKOverlay";
import { FileEditor } from "../FileEditor";
import { EditorStatusBar } from "../EditorStatusBar";
import { TerminalWorkspace } from "./TerminalWorkspace";
import { PreviewPane } from "./PreviewPane";
import { ProjectDevServerForm } from "./ProjectDevServerForm";
import { api } from "../../api/client";
import { api as convexApi } from "../../lib/convexApi";
import { useEntitlements } from "../../hooks/useEntitlements";
import { filesStore } from "../../stores/filesStore";
import { chatsStore } from "../../stores/chatsStore";
import type {
  AgentSession,
  WorkbenchContainer,
  WorkbenchSnapshot,
  AgentProfile,
} from "../../types";
import type { MaxMode } from "./types";
import type { MeterEvent } from "../../hooks/useEditorEdit";
import type { EditorView } from "@codemirror/view";
import type { ResolveCtx, ResolvedRef } from "../../utils/fileRefResolve";
import { renderSafeMarkdown } from "../../markdown";

interface OpenFileTab {
  id: string;
  path: string;
  rootPath: string;
  projectId: string | null;
  content: string;
  savedContent: string;
  status: string;
  restricted: boolean;
  restrictedReason?: string | null;
  loading: boolean;
  error: string | null;
}

interface ActiveRoot {
  projectId: string | null;
  rootPath: string;
  name: string;
}

const workflowSteps = [
  ["Plan", "Scope and policy"],
  ["Inspect", "Read context"],
  ["Patch", "Apply edits"],
  ["Verify", "Tests and checks"],
  ["Review", "Risk brief"],
  ["Ship", "Handoff"],
];

function agentOptions(names: string[]) {
  const defaults = ["Codex", "Claude Code", "Cursor", "Gemini CLI", "Grok", "Regular Terminal"];
  return Array.from(new Set([...names, ...defaults]));
}

function promptRecipesFor(
  session: AgentSession | null
): Array<{ id: string; title: string; kind: string; text: string }> {
  const recipes = (session?.promptRecipes as Array<Record<string, unknown>> | undefined) || [];
  if (recipes.length) {
    return recipes.map((recipe) => ({
      id: String(recipe.id || recipe.title),
      title: String(recipe.title || "Recipe"),
      kind: String(recipe.kind || "Recipe"),
      text: String(recipe.text || ""),
    }));
  }
  return [
    {
      id: "inspect-risk",
      title: "Inspect risk",
      kind: "Recipe",
      text: "Inspect this task for security and implementation risks before editing.",
    },
    {
      id: "write-tests",
      title: "Write tests",
      kind: "Recipe",
      text: "Add or update the focused tests for this change and explain the coverage.",
    },
    {
      id: "continue",
      title: "Continue",
      kind: "Recipe",
      text: "Continue from the current context and keep changes scoped.",
    },
    {
      id: "pr-brief",
      title: "PR brief",
      kind: "Recipe",
      text: "Prepare a reviewer brief with files changed, risk areas, tests, and remaining checks.",
    },
  ];
}

function workflowFor(
  session: AgentSession | null
): Array<{ label: string; description: string; state?: string }> {
  const workflow = (session?.workflow as Array<Record<string, unknown>> | undefined) || [];
  if (workflow.length) {
    return workflow.map((stage) => ({
      label: String(stage.label || stage.name || "Stage"),
      description: String(stage.description || stage.detail || ""),
      state: String(stage.state || ""),
    }));
  }
  return workflowSteps.map(([label, description], index) => ({
    label,
    description,
    state: index === 0 ? "current" : "",
  }));
}

function renderMarkdown(source: string): string {
  return renderSafeMarkdown(source);
}

export interface EditorFrameProps {
  workbenches: { items: WorkbenchContainer[]; activeId: string };
  activeSession: AgentSession | null;
  activeRoot: ActiveRoot | null;
  maxMode: MaxMode;
  onSetMaxMode: (mode: MaxMode) => void;
  // Whether the terminal overlay is shown. Forwarded to TerminalWorkspace so
  // the terminal stops covering the chat/editor when the user selects a chat
  // or opens a file.
  terminalVisible: boolean;
  onSetTerminalVisible: (value: boolean) => void;
  chatVisible: boolean;
  onSetChatVisible: (value: boolean) => void;
  activeChatId: string | null;
  // Chats open as panes in the grid (chatsStore.openChats).
  openChats: string[];
  onSetContextCollapsed: (value: boolean) => void;
  activePane: string;
  onSetActivePane: (pane: string) => void;
  openFiles: OpenFileTab[];
  error: string | null;
  streaming: boolean;
  draftRepo: string;
  onSetDraftRepo: (value: string) => void;
  draftAgent: string;
  onSetDraftAgent: (value: string) => void;
  draftMode: string;
  onSetDraftMode: (value: string) => void;
  draftGoal: string;
  onSetDraftGoal: (value: string) => void;
  messageDraft: string;
  onSetMessageDraft: (value: string) => void;
  modelOverride: string;
  onSetModelOverride: (value: string) => void;
  activeChatAbort: RefObject<AbortController | null>;
  editorWrap: Record<string, boolean>;
  onSetEditorWrap: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  editorCursor: Record<string, { line: number; col: number; selection: number }>;
  onSetEditorCursor: (
    updater: (
      prev: Record<string, { line: number; col: number; selection: number }>
    ) => Record<string, { line: number; col: number; selection: number }>
  ) => void;
  cmdKOpen: boolean;
  onSetCmdKOpen: (value: boolean) => void;
  activeEditorView: EditorView | null;
  onRegisterEditorView: (fileId: string, view: EditorView | null) => void;
  onOpenFileRef?: (ref: ResolvedRef) => void;
  fileRefCtx?: ResolveCtx;
  lastMeter: MeterEvent | null;
  onSetLastMeter: (meter: MeterEvent | null) => void;
  activeFile: OpenFileTab | null;
  snapshot: WorkbenchSnapshot | null;
  terminalAgents: AgentProfile[];
  workbenchActiveId: string;
  onCreateSession: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onSendMessage: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onSaveFile: (tab: OpenFileTab, approved?: boolean, approvalToken?: string) => Promise<void>;
  onCloseFile: (id: string) => void;
  onPatchOpenFile: (id: string, patch: Partial<OpenFileTab>) => void;
  filesProjects: import("../../types").ProjectRecord[];
  onOpenInBrowser: (file: { path: string; projectId: string | null }) => void;
}

export function EditorFrame({
  workbenches,
  activeSession,
  activeRoot,
  maxMode,
  onSetMaxMode,
  terminalVisible,
  onSetTerminalVisible,
  chatVisible,
  onSetChatVisible,
  activeChatId,
  openChats,
  onSetContextCollapsed,
  activePane,
  onSetActivePane,
  openFiles,
  error,
  streaming,
  draftRepo,
  onSetDraftRepo,
  draftAgent,
  onSetDraftAgent,
  draftMode,
  onSetDraftMode,
  draftGoal,
  onSetDraftGoal,
  messageDraft,
  onSetMessageDraft,
  modelOverride,
  onSetModelOverride,
  activeChatAbort,
  editorWrap,
  onSetEditorWrap,
  editorCursor,
  onSetEditorCursor,
  cmdKOpen,
  onSetCmdKOpen,
  activeEditorView,
  onRegisterEditorView,
  onOpenFileRef,
  fileRefCtx,
  lastMeter,
  onSetLastMeter,
  activeFile,
  snapshot,
  terminalAgents,
  workbenchActiveId,
  onCreateSession,
  onSendMessage,
  onSaveFile,
  onCloseFile,
  onPatchOpenFile,
  filesProjects,
  onOpenInBrowser,
}: EditorFrameProps) {
  // Per-file preview tab state: fileId → true means Preview tab is active
  const [previewActiveMap, setPreviewActiveMap] = useState<Record<string, boolean>>({});
  // When set, ProjectDevServerForm replaces the preview pane for that project.
  const [devServerFormFor, setDevServerFormFor] = useState<string | null>(null);

  // PolicyChip enforcement state. The legacy source
  // (snapshot.adminConfig.defaultEnforce, from the 410'd /api/workbench
  // REST bootstrap) no longer exists — the Convex workbench snapshot has
  // no adminConfig, which left the chip permanently in its loading state.
  // Derive it from policy.queries.getEnforcement instead. That query is
  // plan:"security"-gated, so skip it for non-security orgs (their policy
  // engine is off → nothing is enforced → false). undefined = still
  // loading, which PolicyChip renders as the neutral "Policy" state.
  const entitlements = useEntitlements();
  const hasSecurity = entitlements.kind === "ready" && entitlements.hasSecurity;
  const enforcementData = useQuery(
    (convexApi as any).policy.queries.getEnforcement,
    hasSecurity ? {} : "skip"
  ) as { mode?: string } | undefined;
  const defaultEnforce =
    entitlements.kind === "loading"
      ? undefined
      : hasSecurity
        ? !enforcementData
          ? undefined
          : enforcementData.mode === "ask" || enforcementData.mode === "block"
        : false;

  return (
    <main className="chat-workspace">
      <header className="workspace-topbar">
        <div>
          <p className="eyebrow">Workbench</p>
          <h1>{activeRoot ? activeRoot.name : "Steerly"}</h1>
          <p className="chat-project-line">
            {activeRoot ? activeRoot.rootPath : "No project open"}
          </p>
        </div>
        <div className="topbar-actions">
          <button
            className="button secondary inspector-button"
            type="button"
            onClick={() => onSetContextCollapsed(false)}
          >
            Inspector
          </button>
        </div>
      </header>

      <nav className="workspace-tabs" role="tablist" aria-label="Workspace tabs">
        <button
          className={`workspace-tab ${activePane === "chat" ? "is-active" : ""}`}
          role="tab"
          aria-selected={activePane === "chat"}
          type="button"
          onClick={() => onSetActivePane("chat")}
        >
          <MessageCircle size={13} />
          <span>Chat</span>
        </button>
        {openFiles.map((file) => (
          <div
            key={file.id}
            className={`workspace-tab ${activePane === file.id ? "is-active" : ""} ${file.content !== file.savedContent ? "is-dirty" : ""}`}
            role="tab"
            aria-selected={activePane === file.id}
            tabIndex={0}
            onClick={() => onSetActivePane(file.id)}
            onKeyDown={(event) => {
              // Only the tab itself — not Enter/Space bubbling up from the
              // nested close <button> (else we'd preventDefault its own keyboard
              // activation and switch panes instead of closing).
              if (
                event.target === event.currentTarget &&
                (event.key === "Enter" || event.key === " ")
              ) {
                event.preventDefault();
                onSetActivePane(file.id);
              }
            }}
          >
            <FileTypeIcon path={file.path} size={13} />
            <span className="workspace-tab__label">{file.path.split("/").pop()}</span>
            <span className="workspace-tab__dirty" aria-hidden="true">
              *
            </span>
            {/* real <button> (not a span-as-button inside a button) */}
            <button
              type="button"
              className="workspace-tab__close"
              aria-label="Close tab"
              onClick={(event) => {
                event.stopPropagation();
                onCloseFile(file.id);
              }}
            >
              x
            </button>
          </div>
        ))}
      </nav>

      <div className="workspace-panes">
        {/* Only let ChatPanel claim the workspace surface when the user is
              actually on the Chat tab. Without the activePane guard, clicking
              a file in the tree would update openFiles + activePane but the
              center surface stayed pinned to ChatPanel, so the editor never
              rendered (the tab strip showed the file tab to nowhere). */}
        {openChats.length > 0 && activePane === "chat" ? (
          maxMode === "chat" && activeChatId ? (
            // Enlarged: render only the focused chat full-screen (the
            // position:fixed overlay assumes a single .chat-panel).
            <ChatPanel
              chatId={activeChatId}
              isMaxed
              onEnlarge={() => onSetMaxMode("none")}
              onClose={() => {
                // Closing the maximized chat must also leave max mode —
                // otherwise [data-max-mode="chat"] keeps both rails
                // collapsed over the (now chat-less) Welcome/editor. (M2,
                // the chat-side sibling of Codex C1.)
                chatsStore.closeChat(activeChatId);
                onSetMaxMode("none");
              }}
              onOpenFileRef={onOpenFileRef}
              fileRefCtx={fileRefCtx}
            />
          ) : (
            // Grid: every open chat side-by-side (like the terminal grid).
            <div className="chat-grid" data-count={Math.min(openChats.length, 4)}>
              {openChats.map((id) => (
                <ChatPanel
                  key={id}
                  chatId={id}
                  onEnlarge={() => {
                    chatsStore.setActive(id);
                    onSetMaxMode("chat");
                  }}
                  onClose={() => chatsStore.closeChat(id)}
                  onOpenFileRef={onOpenFileRef}
                  fileRefCtx={fileRefCtx}
                />
              ))}
            </div>
          )
        ) : (
          <>
            <section
              className="workspace-pane session-body"
              data-pane="chat"
              data-active={activePane === "chat" ? "true" : undefined}
            >
              <section className="workflow-strip" aria-label="Agent workflow">
                {workflowFor(activeSession).map((stage, index) => (
                  <div
                    key={stage.label}
                    className={`workflow-step ${stage.state || (index === 0 ? "current" : "")}`}
                  >
                    <strong>{stage.label}</strong>
                    <span>{stage.description}</span>
                  </div>
                ))}
              </section>
              <div className="message-list">
                {(activeSession?.messages || []).map((message) => (
                  <article key={message.id} className={`message ${message.role}`}>
                    <header className="message-header">
                      <span className="message-role">{message.role}</span>
                    </header>
                    <div
                      className="message-content"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
                    />
                  </article>
                ))}
                {error && <p className="route-error">{error}</p>}
              </div>
              <section className="composer-shell codex-chat-shell">
                <div className="prompt-recipes" aria-label="Prompt recipes">
                  {promptRecipesFor(activeSession).map((recipe) => (
                    <button
                      key={recipe.id}
                      type="button"
                      className="recipe-button"
                      onClick={() => onSetMessageDraft(recipe.text)}
                    >
                      <strong>{recipe.title}</strong>
                      <span>{recipe.kind}</span>
                    </button>
                  ))}
                </div>
                <form className="quick-session codex-session-composer" onSubmit={onCreateSession}>
                  <div className="quick-session-controls quick-session-controls--top">
                    <select
                      value={draftRepo}
                      onChange={(event) => onSetDraftRepo(event.target.value)}
                      aria-label="Repository"
                    >
                      <option value="">Local repo</option>
                      {filesProjects.map((project) => (
                        <option key={project.id} value={project.name || project.rootPath}>
                          {project.name || project.rootPath}
                        </option>
                      ))}
                    </select>
                    <select
                      value={draftAgent}
                      onChange={(event) => onSetDraftAgent(event.target.value)}
                      aria-label="Agent"
                    >
                      {agentOptions(terminalAgents.map((agent) => agent.name)).map((agent) => (
                        <option key={agent} value={agent}>
                          {agent}
                        </option>
                      ))}
                    </select>
                    <select
                      value={draftMode}
                      onChange={(event) => onSetDraftMode(event.target.value)}
                      aria-label="Guardrail mode"
                    >
                      <option value="approval-gated">Ship with gates</option>
                      <option value="guided">Repo-safe coding</option>
                      <option value="observe">Review only</option>
                    </select>
                  </div>
                  <textarea
                    value={draftGoal}
                    onChange={(event) => onSetDraftGoal(event.target.value)}
                    rows={2}
                  />
                  <div className="quick-session-actions">
                    <button className="button secondary" type="submit">
                      New session
                    </button>
                  </div>
                </form>
                <form className="composer" onSubmit={onSendMessage}>
                  <textarea
                    value={messageDraft}
                    onChange={(event) => onSetMessageDraft(event.target.value)}
                    rows={3}
                    placeholder="Message the active agent. Ask it to inspect files, explain risk, write tests, continue, or prepare a PR brief."
                  />
                  <div className="composer-aside">
                    <button
                      className="button secondary"
                      type="button"
                      hidden={!streaming}
                      onClick={() => activeChatAbort.current?.abort()}
                    >
                      Stop
                    </button>
                    <select
                      className="composer-model"
                      aria-label="Model override"
                      value={modelOverride}
                      onChange={(event) => onSetModelOverride(event.target.value)}
                    >
                      <option value="">Default model</option>
                      <option value="claude-sonnet-4-6">claude-sonnet-4-6</option>
                      <option value="claude-opus-4-7">claude-opus-4-7</option>
                      <option value="gpt-5">gpt-5</option>
                      <option value="gpt-5-codex">gpt-5-codex</option>
                    </select>
                    <button
                      className="button primary"
                      type="submit"
                      disabled={!activeSession || streaming}
                    >
                      {streaming ? "Streaming" : "Send"}
                    </button>
                  </div>
                </form>
              </section>
            </section>

            {openFiles.map((file) => {
              const wrap = editorWrap[file.id] ?? false;
              const cursor = editorCursor[file.id] || { line: 1, col: 1, selection: 0 };
              const dirty = file.content !== file.savedContent;
              const isHtml = file.path.toLowerCase().endsWith(".html");
              const previewActive = isHtml && (previewActiveMap[file.id] ?? false);
              return (
                <section
                  key={file.id}
                  className="workspace-pane workspace-pane--file"
                  data-pane={`file:${file.id}`}
                  data-active={activePane === file.id ? "true" : undefined}
                >
                  <header className="editor-bar">
                    <div className="editor-bar__breadcrumb">
                      <span className="editor-bar__path">{file.path}</span>
                      <span className="editor-bar__status">{file.error || file.status}</span>
                    </div>
                    {isHtml && (
                      <div className="editor-frame__tabstrip">
                        <button
                          type="button"
                          className={`editor-tab${!previewActive ? " is-active" : ""}`}
                          onClick={() =>
                            setPreviewActiveMap((prev) => ({ ...prev, [file.id]: false }))
                          }
                        >
                          Source
                        </button>
                        <button
                          type="button"
                          className={`editor-tab${previewActive ? " is-active" : ""}`}
                          onClick={() =>
                            setPreviewActiveMap((prev) => ({ ...prev, [file.id]: true }))
                          }
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          className="editor-tab"
                          title="Open in the browser pane"
                          onClick={() =>
                            onOpenInBrowser({
                              path: file.path,
                              projectId: file.projectId ?? null,
                            })
                          }
                        >
                          Open in Browser
                        </button>
                      </div>
                    )}
                    <div className="editor-bar__actions">
                      <button
                        className="button primary"
                        type="button"
                        disabled={file.loading || !dirty}
                        onClick={() => void onSaveFile(file)}
                      >
                        Save
                      </button>
                    </div>
                  </header>
                  {previewActive ? (
                    devServerFormFor && devServerFormFor === file.projectId ? (
                      <ProjectDevServerForm
                        projectId={file.projectId ?? ""}
                        initialUrl={
                          filesProjects.find((p) => p.id === file.projectId)?.devServerUrl ?? null
                        }
                        onSave={async (url) => {
                          const projectId = file.projectId;
                          if (!projectId) return;
                          const result = await api.setProjectDevServerUrl(projectId, url);
                          filesStore.setState((current) => ({
                            ...current,
                            projects: current.projects.map((p) =>
                              p.id === projectId ? { ...p, devServerUrl: result.devServerUrl } : p
                            ),
                          }));
                          setDevServerFormFor(null);
                        }}
                        onCancel={() => setDevServerFormFor(null)}
                      />
                    ) : (
                      <PreviewPane
                        projectId={file.projectId ?? ""}
                        filePath={file.path}
                        devServerUrl={
                          filesProjects.find((p) => p.id === file.projectId)?.devServerUrl ?? null
                        }
                        onConfigureDevServer={() => {
                          setDevServerFormFor(file.projectId ?? null);
                        }}
                      />
                    )
                  ) : (
                    <>
                      <div className="editor-host">
                        {file.error && <p className="inline-error">{file.error}</p>}
                        {!file.error && (
                          <FileEditor
                            path={file.path}
                            content={file.content}
                            readOnly={file.loading}
                            wrapLines={wrap}
                            onChange={(next) =>
                              onPatchOpenFile(file.id, {
                                content: next,
                                status: next === file.savedContent ? file.status : "Modified",
                              })
                            }
                            onSave={() => {
                              if (dirty && !file.loading) void onSaveFile(file);
                            }}
                            onCursor={(info) =>
                              onSetEditorCursor((prev) =>
                                prev[file.id]?.line === info.line &&
                                prev[file.id]?.col === info.col &&
                                prev[file.id]?.selection === info.selection
                                  ? prev
                                  : { ...prev, [file.id]: info }
                              )
                            }
                            onReady={(view) => onRegisterEditorView(file.id, view)}
                          />
                        )}
                      </div>
                      {!file.error && (
                        <EditorStatusBar
                          path={file.path}
                          line={cursor.line}
                          col={cursor.col}
                          selection={cursor.selection}
                          dirty={dirty}
                          wrapLines={wrap}
                          onToggleWrap={() =>
                            onSetEditorWrap((prev) => ({ ...prev, [file.id]: !prev[file.id] }))
                          }
                          meter={activePane === file.id ? lastMeter : undefined}
                        />
                      )}
                    </>
                  )}
                </section>
              );
            })}
          </>
        )}
      </div>

      {cmdKOpen && activeFile && (
        <CmdKOverlay
          view={activeEditorView}
          filePath={activeFile.path}
          fileContent={activeFile.content}
          open={cmdKOpen}
          onClose={() => onSetCmdKOpen(false)}
          onMeterUpdate={onSetLastMeter}
        />
      )}

      <TerminalWorkspace
        maxMode={maxMode}
        paneVisible={terminalVisible}
        onClose={() => {
          onSetTerminalVisible(false);
          // If the terminal was maximized, closing it must also exit max
          // mode — [data-max-mode="terminal"] keeps both rails collapsed,
          // so leaving maxMode set blanks the workspace with no rails.
          // (Codex C1.)
          if (maxMode === "terminal") onSetMaxMode("none");
        }}
        workbenchId={workbenchActiveId}
        onMinimize={() => onSetMaxMode("none")}
        onMaximize={() => onSetMaxMode("terminal")}
        activeSessionId={activeSession?.id || null}
        projectId={activeRoot?.projectId || null}
        rootPath={activeRoot?.rootPath || null}
        defaultEnforce={defaultEnforce}
        onOpenFileRef={onOpenFileRef}
      />
    </main>
  );
}
